import { useAgentChat } from "@cloudflare/ai-chat/react";
import { useAgent } from "agents/react";
import {
  ArrowRight,
  BarChart3,
  BookOpen,
  Check,
  ChevronDown,
  CircleAlert,
  Coins,
  Database,
  ExternalLink,
  Gauge,
  History,
  LockKeyhole,
  LogOut,
  Menu,
  MessageCircle,
  Mic,
  Plus,
  Send,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import {
  FormEvent,
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ARCHIVE_SOURCES,
  type ArchiveLanguage,
  type ArchiveSource,
  type ConversationSummary,
} from "../shared/archive";

const copy = {
  en: {
    archive: "Living Archive",
    navAbout: "About Dmitry",
    navShop: "Bruderkuss.art",
    eyebrow: "12,954 searchable documents · one life",
    titleA: "Ask what",
    titleB: "remains.",
    intro:
      "A family archive of Dmitry Vrubel’s life, work and words. Every answer returns to evidence.",
    placeholder: "Ask about a work, memory, year or idea…",
    notice: "Citations always on · Uncertainty stays visible",
    about: "Biography",
    voice: "From the archive",
    aboutHelp: "Biographical, third-person answers",
    voiceHelp: "First-person reconstruction from primary sources",
    reconstruction:
      "AI reconstruction from Dmitry’s archive. This is not Dmitry and does not create new memories.",
    newChat: "New conversation",
    history: "Conversations",
    noHistory: "Your conversations will appear here.",
    sources: "Archive sources",
    source: "source",
    sourcesPlural: "sources",
    suggested: "Ways into the archive",
    thinking: "Reading the archive",
    emptyAnswer:
      "The archive model did not complete this answer. Please ask again.",
    localPreview: "Grounded preview",
    localPreviewHelp:
      "The interface works without a model key. Production uses the same citation contract with a frontier model.",
    emptyTitle: "Begin anywhere.",
    emptyBody:
      "Ask something factual, emotional or difficult. If the archive cannot establish it, it will say so.",
    delete: "Delete",
    close: "Close",
    confidence: "Evidence",
    machine: "Machine transcript",
    conflict: "Unresolved conflict",
    openSource: "Open original",
    footer:
      "Built by the Vrubel family. An archive is a relationship, not a simulation.",
    suggestions: [
      "Why did the Bruderkuss become so powerful?",
      "What did Dmitry say about virtual reality?",
      "Where was his Moscow apartment gallery?",
      "What did he say after the invasion of Ukraine?",
    ],
  },
  de: {
    archive: "Lebendiges Archiv",
    navAbout: "Über Dmitri",
    navShop: "Bruderkuss.art",
    eyebrow: "12.954 durchsuchbare Dokumente · ein Leben",
    titleA: "Frag, was",
    titleB: "bleibt.",
    intro:
      "Ein Familienarchiv über Dmitri Vrubels Leben, Werk und Worte. Jede Antwort führt zurück zum Beleg.",
    placeholder: "Frag nach einem Werk, einer Erinnerung, einem Jahr…",
    notice: "Immer mit Quellen · Unsicherheit bleibt sichtbar",
    about: "Biografie",
    voice: "Aus dem Archiv",
    aboutHelp: "Biografische Antworten in der dritten Person",
    voiceHelp: "Ich-Rekonstruktion aus Primärquellen",
    reconstruction:
      "KI-Rekonstruktion aus Dmitris Archiv. Dies ist nicht Dmitri und erzeugt keine neuen Erinnerungen.",
    newChat: "Neues Gespräch",
    history: "Gespräche",
    noHistory: "Deine Gespräche erscheinen hier.",
    sources: "Archivquellen",
    source: "Quelle",
    sourcesPlural: "Quellen",
    suggested: "Wege ins Archiv",
    thinking: "Im Archiv lesen",
    emptyAnswer:
      "Das Archivmodell konnte diese Antwort nicht abschließen. Bitte frage noch einmal.",
    localPreview: "Belegbasierte Vorschau",
    localPreviewHelp:
      "Die Oberfläche funktioniert ohne Modellschlüssel. Die Produktion nutzt denselben Quellenvertrag mit einem Frontier-Modell.",
    emptyTitle: "Beginne irgendwo.",
    emptyBody:
      "Frag sachlich, emotional oder schwierig. Wenn das Archiv etwas nicht belegt, sagt es das.",
    delete: "Löschen",
    close: "Schließen",
    confidence: "Belege",
    machine: "Maschinelles Transkript",
    conflict: "Offener Widerspruch",
    openSource: "Original öffnen",
    footer:
      "Von der Familie Vrubel. Ein Archiv ist eine Beziehung, keine Simulation.",
    suggestions: [
      "Warum wurde der Bruderkuss so kraftvoll?",
      "Was sagte Dmitri über virtuelle Realität?",
      "Wo war seine Moskauer Wohnungsgalerie?",
      "Was sagte er nach dem Angriff auf die Ukraine?",
    ],
  },
  ru: {
    archive: "Живой архив",
    navAbout: "О Дмитрии",
    navShop: "Bruderkuss.art",
    eyebrow: "12 954 документа в поиске · одна жизнь",
    titleA: "Спроси о том,",
    titleB: "что осталось.",
    intro:
      "Семейный архив жизни, работ и слов Дмитрия Врубеля. Каждый ответ возвращается к источникам.",
    placeholder: "Спросите о работе, воспоминании, годе или идее…",
    notice: "Всегда с источниками · Неопределённость видна",
    about: "Биография",
    voice: "Из архива",
    aboutHelp: "Биографические ответы от третьего лица",
    voiceHelp: "Реконструкция от первого лица по первичным источникам",
    reconstruction:
      "ИИ-реконструкция по архиву Дмитрия. Это не Дмитрий, и система не создаёт новых воспоминаний.",
    newChat: "Новый разговор",
    history: "Разговоры",
    noHistory: "Здесь появится история разговоров.",
    sources: "Источники архива",
    source: "источник",
    sourcesPlural: "источников",
    suggested: "Входы в архив",
    thinking: "Читаю архив",
    emptyAnswer:
      "Модель архива не завершила ответ. Пожалуйста, задайте вопрос ещё раз.",
    localPreview: "Версия с источниками",
    localPreviewHelp:
      "Интерфейс работает без ключа модели. В продакшене тот же контракт цитирования используется с передовой моделью.",
    emptyTitle: "Начните с любого места.",
    emptyBody:
      "Можно спросить о факте, чувстве или сложной теме. Если архив не даёт ответа, он скажет об этом.",
    delete: "Удалить",
    close: "Закрыть",
    confidence: "Источники",
    machine: "Машинная расшифровка",
    conflict: "Открытое противоречие",
    openSource: "Открыть оригинал",
    footer:
      "Создано семьёй Врубель. Архив — это отношение, а не симуляция.",
    suggestions: [
      "Почему «Братский поцелуй» стал таким сильным образом?",
      "Что Дмитрий говорил о виртуальной реальности?",
      "Где находилась его квартирная галерея?",
      "Что он сказал после вторжения в Украину?",
    ],
  },
} as const;

async function archiveFetch(path: string, init?: RequestInit) {
  return fetch(path, {
    ...init,
    credentials: "same-origin",
    headers: {
      "content-type": "application/json",
      ...init?.headers,
    },
  });
}

function LoginGate({
  language,
  setLanguage,
  onAuthenticated,
}: {
  language: ArchiveLanguage;
  setLanguage: (language: ArchiveLanguage) => void;
  onAuthenticated: (role: "family" | "admin") => void;
}) {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const words = {
    en: {
      eyebrow: "Private family archive",
      title: "Enter the archive.",
      body: "This space contains family research material. Use the access code shared with you by the Vrubel family.",
      placeholder: "Family access code",
      submit: "Continue",
    },
    de: {
      eyebrow: "Privates Familienarchiv",
      title: "Archiv betreten.",
      body: "Dieser Raum enthält familiäres Forschungsmaterial. Verwende den Zugangscode der Familie Vrubel.",
      placeholder: "Familien-Zugangscode",
      submit: "Weiter",
    },
    ru: {
      eyebrow: "Частный семейный архив",
      title: "Войти в архив.",
      body: "Здесь находятся семейные исследовательские материалы. Используйте код доступа, полученный от семьи Врубель.",
      placeholder: "Код семейного доступа",
      submit: "Продолжить",
    },
  }[language];

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!code.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      const response = await archiveFetch("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ code }),
      });
      const result = (await response.json()) as {
        authenticated?: boolean;
        role?: "family" | "admin";
        error?: string;
      };
      if (!response.ok || !result.authenticated) {
        setError(result.error || "Access could not be verified.");
        return;
      }
      setCode("");
      onAuthenticated(result.role === "admin" ? "admin" : "family");
    } catch {
      setError("The archive could not be reached. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="access-gate">
      <div className="ambient ambient--one" />
      <div className="ambient ambient--two" />
      <header className="access-gate__header">
        <a href="https://bruderkuss.art" className="landing-nav__brand">
          <ArchiveMark small />
          <span>
            <AnimatedName />
            <small>Living Archive</small>
          </span>
        </a>
        <LanguagePicker language={language} setLanguage={setLanguage} />
      </header>
      <section className="access-card">
        <span className="access-card__lock">
          <LockKeyhole size={20} />
        </span>
        <p className="eyebrow">
          <span />
          {words.eyebrow}
        </p>
        <h1>{words.title}</h1>
        <p>{words.body}</p>
        <form onSubmit={submit}>
          <input
            type="password"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            placeholder={words.placeholder}
            autoComplete="current-password"
            autoFocus
          />
          <button type="submit" disabled={!code.trim() || busy}>
            {busy ? "…" : words.submit}
            <ArrowRight size={16} />
          </button>
        </form>
        {error && <p className="access-card__error">{error}</p>}
        <small>Encrypted session · 30 days · archive evidence stays private</small>
      </section>
    </main>
  );
}

function newConversation(
  language: ArchiveLanguage,
  title = "New conversation",
): ConversationSummary {
  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    title,
    mode: "about",
    language,
    createdAt: now,
    updatedAt: now,
  };
}

function messageText(
  parts: ReadonlyArray<{ type: string; text?: string }>,
): string {
  return parts
    .filter((part) => part.type === "text")
    .map((part) => part.text ?? "")
    .join("");
}

function citedSourceIds(text: string): string[] {
  const ids = [...text.matchAll(/\[([ACS]\d+(?:\s*,\s*[ACS]\d+)*)\]/g)]
    .flatMap((match) => match[1].match(/[ACS]\d+/g) || []);
  return [...new Set(ids)];
}

function displayAnswerText(text: string): string {
  return text
    .replace(/\s*\[(?:[ACS]\d+)(?:\s*,\s*[ACS]\d+)*\]/g, "")
    .replace(/\s*—\s*(?=[«“"])/g, ": ")
    .replace(/\s*—\s*/g, ", ")
    .replace(/[ \t]+([,.;:!?])/g, "$1")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
}

function sourceDescriptor(
  source: ArchiveSource,
  language: ArchiveLanguage,
): string {
  const identity = `${source.label} ${source.sourceType}`.toLowerCase();
  const kind =
    source.sourceType === "claim"
      ? "claim"
      : identity.includes("facebook")
        ? "facebook"
        : identity.includes("livejournal")
          ? "livejournal"
          : identity.includes("interview")
            ? "interview"
            : identity.includes("transcript") || identity.includes("video")
              ? "transcript"
              : identity.includes("pdf")
                ? "document"
                : "archive";

  const labels: Record<ArchiveLanguage, Record<typeof kind, string>> = {
    en: {
      claim: "Verified archive fact",
      facebook: "Dmitry's Facebook post",
      livejournal: "Dmitry's LiveJournal post",
      interview: "Interview",
      transcript: "Video transcript",
      document: "Archive document",
      archive: "Archive source",
    },
    de: {
      claim: "Geprüfter Archivfakt",
      facebook: "Dmitris Facebook-Beitrag",
      livejournal: "Dmitris LiveJournal-Beitrag",
      interview: "Interview",
      transcript: "Video-Transkript",
      document: "Archivdokument",
      archive: "Archivquelle",
    },
    ru: {
      claim: "Проверенный факт архива",
      facebook: "Пост Димы в Facebook",
      livejournal: "Пост Димы в LiveJournal",
      interview: "Интервью",
      transcript: "Расшифровка видео",
      document: "Документ из архива",
      archive: "Источник из архива",
    },
  };

  return labels[language][kind];
}

function LanguagePicker({
  language,
  setLanguage,
}: {
  language: ArchiveLanguage;
  setLanguage: (language: ArchiveLanguage) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="language-picker">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-label="Change language"
      >
        {language.toUpperCase()}
        <ChevronDown size={13} />
      </button>
      {open && (
        <div className="language-picker__menu">
          {(["en", "de", "ru"] as ArchiveLanguage[]).map((item) => (
            <button
              type="button"
              key={item}
              onClick={() => {
                setLanguage(item);
                setOpen(false);
              }}
            >
              {item === "en" ? "English" : item === "de" ? "Deutsch" : "Русский"}
              {item === language && <Check size={13} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ArchiveMark({ small = false }: { small?: boolean }) {
  return (
    <div className={`archive-mark ${small ? "archive-mark--small" : ""}`}>
      <span>D</span>
      <i />
      <span>V</span>
    </div>
  );
}

function AnimatedName() {
  return (
    <span className="animated-name" aria-label="Dmitry Vrubel">
      <span className="animated-name__cyrillic" aria-hidden="true">
        ДМИТРИЙ ВРУБЕЛЬ
      </span>
      <span className="animated-name__english" aria-hidden="true">
        DMITRY VRUBEL
      </span>
    </span>
  );
}

function BiographyTab({
  language,
  compact = false,
}: {
  language: ArchiveLanguage;
  compact?: boolean;
}) {
  return (
    <div className={`biography-tab ${compact ? "biography-tab--compact" : ""}`}>
      <BookOpen size={compact ? 13 : 15} />
      {copy[language].about}
    </div>
  );
}

function Composer({
  value,
  setValue,
  onSubmit,
  placeholder,
  large = false,
  disabled = false,
}: {
  value: string;
  setValue: (value: string) => void;
  onSubmit: () => void;
  placeholder: string;
  large?: boolean;
  disabled?: boolean;
}) {
  const [listening, setListening] = useState(false);
  const submit = (event?: FormEvent) => {
    event?.preventDefault();
    if (value.trim() && !disabled) onSubmit();
  };
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };
  const listen = () => {
    const SpeechRecognition =
      (
        window as unknown as {
          webkitSpeechRecognition?: new () => {
            lang: string;
            start: () => void;
            onresult: (event: {
              results: ArrayLike<{ 0: { transcript: string } }>;
            }) => void;
            onend: () => void;
          };
        }
      ).webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    const recognition = new SpeechRecognition();
    recognition.lang = document.documentElement.lang || "en";
    recognition.onresult = (event) => {
      setValue(event.results[0][0].transcript);
    };
    recognition.onend = () => setListening(false);
    setListening(true);
    recognition.start();
  };

  return (
    <form
      className={`composer ${large ? "composer--large" : ""}`}
      onSubmit={submit}
    >
      <Sparkles className="composer__spark" size={large ? 20 : 17} />
      <textarea
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        rows={1}
        aria-label={placeholder}
      />
      <button
        type="button"
        className={`composer__mic ${listening ? "is-listening" : ""}`}
        onClick={listen}
        aria-label="Use voice input"
      >
        <Mic size={large ? 19 : 17} />
      </button>
      <button
        type="submit"
        className="composer__send"
        disabled={!value.trim() || disabled}
        aria-label="Send"
      >
        {large ? <ArrowRight size={20} /> : <Send size={16} />}
      </button>
    </form>
  );
}

function Landing({
  language,
  setLanguage,
  onStart,
  onLogout,
  isAdmin,
}: {
  language: ArchiveLanguage;
  setLanguage: (language: ArchiveLanguage) => void;
  onStart: (question: string) => void;
  onLogout: () => void;
  isAdmin: boolean;
}) {
  const [question, setQuestion] = useState("");
  const t = copy[language];

  return (
    <main className="landing">
      <div className="ambient ambient--one" />
      <div className="ambient ambient--two" />
      <nav className="landing-nav">
        <a href="/" className="landing-nav__brand" aria-label="Dmitry Vrubel archive">
          <ArchiveMark small />
          <span>
            <AnimatedName />
            <small>{t.archive}</small>
          </span>
        </a>
        <div className="landing-nav__links">
          <a href="https://bruderkuss.art/dmitry-vrubel">{t.navAbout}</a>
          <a href="https://bruderkuss.art" className="shop-link">
            {t.navShop}
            <ExternalLink size={12} />
          </a>
          {isAdmin && (
            <a href="/admin" className="admin-link">
              Admin
            </a>
          )}
          <LanguagePicker language={language} setLanguage={setLanguage} />
          <button
            type="button"
            className="logout-button"
            onClick={onLogout}
            aria-label="Sign out"
          >
            <LogOut size={14} />
          </button>
        </div>
      </nav>

      <section className="hero">
        <div className="hero__index" aria-hidden="true">
          <span>1960</span>
          <i />
          <span>2022</span>
        </div>
        <div className="hero__content">
          <p className="eyebrow">
            <span />
            {t.eyebrow}
          </p>
          <h1>
            <span>{t.titleA}</span>
            <em>{t.titleB}</em>
          </h1>
          <p className="hero__intro">{t.intro}</p>

          <div className="hero__mode">
            <BiographyTab language={language} />
            <p>{t.aboutHelp}</p>
          </div>

          <div className="hero__ask">
            <Composer
              value={question}
              setValue={setQuestion}
              onSubmit={() => onStart(question)}
              placeholder={t.placeholder}
              large
            />
            <p className="hero__notice">
              <Check size={13} />
              {t.notice}
            </p>
          </div>

          <div className="suggestions">
            <span>{t.suggested}</span>
            <div>
              {t.suggestions.map((suggestion, index) => (
                <button
                  type="button"
                  onClick={() => onStart(suggestion)}
                  key={suggestion}
                >
                  <small>0{index + 1}</small>
                  {suggestion}
                  <ArrowRight size={14} />
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="hero__glyph" aria-hidden="true">
          <span>Д</span>
          <i />
        </div>
      </section>

      <footer className="landing-footer">
        <p>{t.footer}</p>
        <span>Berlin · Moscow · The archive</span>
      </footer>
    </main>
  );
}

function EvidencePanel({
  source,
  onClose,
  language,
}: {
  source: ArchiveSource;
  onClose: () => void;
  language: ArchiveLanguage;
}) {
  const t = copy[language];
  return (
    <aside className="evidence-panel">
      <div className="evidence-panel__header">
        <div>
          <span>{sourceDescriptor(source, language)}</span>
          {source.date && <small>{source.date}</small>}
        </div>
        <button type="button" onClick={onClose} aria-label={t.close}>
          <X size={18} />
        </button>
      </div>
      <div className="evidence-panel__body">
        <p className="eyebrow">
          <span />
          {t.confidence}
        </p>
        <h2>{source.title}</h2>
        <p className="source-label">
          {[source.date, sourceDescriptor(source, language)]
            .filter(Boolean)
            .join(" · ")}
        </p>
        <blockquote>{source.excerpt}</blockquote>
        <div className="evidence-flags">
          {source.machineTranscript && (
            <span className="flag flag--warning">
              <CircleAlert size={13} />
              {t.machine}
            </span>
          )}
          {source.conflict && (
            <span className="flag flag--conflict">
              <CircleAlert size={13} />
              {t.conflict}
            </span>
          )}
          {!source.machineTranscript && !source.conflict && (
            <span className="flag">
              <Check size={13} />
              {sourceDescriptor(source, language)}
            </span>
          )}
        </div>
        {source.url && (
          <a href={source.url} target="_blank" rel="noreferrer">
            {t.openSource}
            <ExternalLink size={14} />
          </a>
        )}
      </div>
    </aside>
  );
}

function ConversationSidebar({
  conversations,
  activeId,
  onSelect,
  onNew,
  onDelete,
  language,
  mobileOpen,
  setMobileOpen,
  onLogout,
  isAdmin,
}: {
  conversations: ConversationSummary[];
  activeId: string;
  onSelect: (id: string) => void;
  onNew: () => void;
  onDelete: (id: string) => void;
  language: ArchiveLanguage;
  mobileOpen: boolean;
  setMobileOpen: (open: boolean) => void;
  onLogout: () => void;
  isAdmin: boolean;
}) {
  const t = copy[language];
  return (
    <>
      {mobileOpen && (
        <button
          className="sidebar-scrim"
          type="button"
          onClick={() => setMobileOpen(false)}
          aria-label={t.close}
        />
      )}
      <aside className={`chat-sidebar ${mobileOpen ? "is-open" : ""}`}>
        <div className="chat-sidebar__brand">
          <ArchiveMark small />
          <span>
            <AnimatedName />
            <small>{t.archive}</small>
          </span>
          <button
            type="button"
            className="chat-sidebar__close"
            onClick={() => setMobileOpen(false)}
            aria-label={t.close}
          >
            <X size={18} />
          </button>
        </div>
        <button type="button" className="new-chat" onClick={onNew}>
          <Plus size={16} />
          {t.newChat}
        </button>
        <div className="chat-sidebar__label">
          <span>{t.history}</span>
          <History size={13} />
        </div>
        <div className="conversation-list">
          {conversations.length === 0 && (
            <p className="conversation-list__empty">{t.noHistory}</p>
          )}
          {conversations.map((conversation) => (
            <div
              className={`conversation-item ${
                conversation.id === activeId ? "is-active" : ""
              }`}
              key={conversation.id}
            >
              <button
                type="button"
                onClick={() => {
                  onSelect(conversation.id);
                  setMobileOpen(false);
                }}
              >
                <MessageCircle size={14} />
                <span>
                  {conversation.title}
                  <small>{t.about}</small>
                </span>
              </button>
              <button
                type="button"
                className="conversation-item__delete"
                onClick={() => onDelete(conversation.id)}
                aria-label={t.delete}
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
        <div className="chat-sidebar__bottom">
          {isAdmin && (
            <a href="/admin">
              Usage admin
              <BarChart3 size={12} />
            </a>
          )}
          <a href="https://bruderkuss.art">
            Bruderkuss.art
            <ExternalLink size={12} />
          </a>
          <button type="button" onClick={onLogout}>
            <LogOut size={12} />
            Family archive
          </button>
        </div>
      </aside>
    </>
  );
}

function ChatSession({
  conversation,
  initialQuestion,
  onInitialQuestionSent,
  onLanguageChange,
  onTitle,
  onSelectSource,
  language,
  setLanguage,
  openSidebar,
  brainLabel,
}: {
  conversation: ConversationSummary;
  initialQuestion: string | null;
  onInitialQuestionSent: () => void;
  onLanguageChange: (language: ArchiveLanguage) => void;
  onTitle: (title: string) => void;
  onSelectSource: (source: ArchiveSource) => void;
  language: ArchiveLanguage;
  setLanguage: (language: ArchiveLanguage) => void;
  openSidebar: () => void;
  brainLabel: string;
}) {
  const [input, setInput] = useState("");
  const [retrievedSources, setRetrievedSources] = useState<
    Record<string, ArchiveSource>
  >({});
  const sentInitial = useRef(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const t = copy[language];
  const agent = useAgent({
    agent: "DmitryArchiveAgent",
    name: conversation.id,
  });
  const { messages, sendMessage, status } = useAgentChat({
    agent,
    body: () => ({
      language,
    }),
  });
  const busy = status === "submitted" || status === "streaming";

  const submitText = (text: string) => {
    const clean = text.trim();
    if (!clean || busy) return;
    sendMessage({
      role: "user",
      parts: [{ type: "text", text: clean }],
    });
    if (!messages.some((message) => message.role === "user")) {
      onTitle(clean.slice(0, 64));
    }
    setInput("");
  };

  useEffect(() => {
    if (
      initialQuestion &&
      !sentInitial.current &&
      status === "ready" &&
      messages.length === 0
    ) {
      sentInitial.current = true;
      submitText(initialQuestion);
      onInitialQuestionSent();
    }
  }, [initialQuestion, messages.length, status]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, status]);

  const citedIds = useMemo(
    () => [
      ...new Set(
        messages.flatMap((message) =>
          citedSourceIds(messageText(message.parts)),
        ),
      ),
    ],
    [messages],
  );

  useEffect(() => {
    const missing = citedIds.filter(
      (id) =>
        !retrievedSources[id] &&
        !ARCHIVE_SOURCES.some((source) => source.id === id),
    );
    if (!missing.length) return;
    const latestQuestion =
      [...messages]
        .reverse()
        .find((message) => message.role === "user")
        ?.parts.map((part) => (part.type === "text" ? part.text : ""))
        .join(" ") || "";
    archiveFetch("/api/evidence", {
      method: "POST",
      body: JSON.stringify({ ids: missing, query: latestQuestion }),
    })
      .then((response) => (response.ok ? response.json() : []))
      .then((sources: ArchiveSource[]) => {
        setRetrievedSources((current) => ({
          ...current,
          ...Object.fromEntries(sources.map((source) => [source.id, source])),
        }));
      })
      .catch(() => undefined);
  }, [citedIds.join(","), retrievedSources]);

  return (
    <section className="chat-main">
      <header className="chat-header">
        <button
          type="button"
          className="mobile-menu"
          onClick={openSidebar}
          aria-label={t.history}
        >
          <Menu size={18} />
        </button>
        <BiographyTab language={language} compact />
        <div className="chat-header__meta">
          <span className="online-dot" />
          {brainLabel}
        </div>
        <LanguagePicker
          language={language}
          setLanguage={(nextLanguage) => {
            setLanguage(nextLanguage);
            onLanguageChange(nextLanguage);
          }}
        />
      </header>

      <div className="message-scroll">
        <div className="message-column">
          {messages.length === 0 && !initialQuestion && (
            <div className="chat-empty">
              <ArchiveMark />
              <p className="eyebrow">
                <span />
                {brainLabel}
              </p>
              <h1>{t.emptyTitle}</h1>
              <p>{t.emptyBody}</p>
              <div>
                {t.suggestions.slice(0, 3).map((suggestion) => (
                  <button
                    type="button"
                    key={suggestion}
                    onClick={() => submitText(suggestion)}
                  >
                    {suggestion}
                    <ArrowRight size={14} />
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((message) => {
            const text = messageText(message.parts);
            const sourceIds = citedSourceIds(text);
            return (
              <article
                key={message.id}
                className={`message message--${message.role}`}
              >
                <div className="message__author">
                  {message.role === "assistant" ? (
                    <>
                      <ArchiveMark small />
                      <span>
                        {t.about}
                        <small>{brainLabel}</small>
                      </span>
                    </>
                  ) : (
                    <span>You</span>
                  )}
                </div>
                <div className="message__content">
                  {message.role === "assistant" ? (
                    text.trim() ? (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {displayAnswerText(text)}
                      </ReactMarkdown>
                    ) : (
                      <p className="message__empty">{t.emptyAnswer}</p>
                    )
                  ) : (
                    <p>{text}</p>
                  )}
                </div>
                {message.role === "assistant" && sourceIds.length > 0 && (
                  <details className="message__sources">
                    <summary>
                      <BookOpen size={13} />
                      <span>{t.sources}</span>
                      <small>{sourceIds.length}</small>
                      <ChevronDown size={13} />
                    </summary>
                    <div className="message__source-list">
                      {sourceIds.map((id) => {
                        const source =
                          retrievedSources[id] ||
                          ARCHIVE_SOURCES.find((item) => item.id === id);
                        if (!source) return null;
                        return (
                          <button
                            type="button"
                            key={id}
                            onClick={() => onSelectSource(source)}
                          >
                            <BookOpen size={12} />
                            <span>
                              {source.title}
                              <small>
                                {[
                                  source.date,
                                  sourceDescriptor(source, language),
                                ]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </small>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </details>
                )}
              </article>
            );
          })}

          {busy && (
            <div className="thinking">
              <ArchiveMark small />
              <span>
                {t.thinking}
                <i />
                <i />
                <i />
              </span>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      </div>

      <div className="chat-composer-wrap">
        <Composer
          value={input}
          setValue={setInput}
          onSubmit={() => submitText(input)}
          placeholder={t.placeholder}
          disabled={busy}
        />
        <p>{t.notice}</p>
      </div>
    </section>
  );
}

interface AdminUsageData {
  days: number;
  model: string;
  fallbacks: string[];
  summary: Record<string, number>;
  daily: Array<Record<string, number | string>>;
  models: Array<Record<string, number | string>>;
  recent: Array<Record<string, number | string>>;
  collections: Array<Record<string, number | string>>;
  privacy: { stored: string; excluded: string };
}

interface AdminChatSummary {
  id: string;
  title: string;
  language: string;
  created_at: string;
  updated_at: string;
  generated_turns: number | string;
  evaluated_turns: number | string;
}

interface AdminMessage {
  id: string;
  role: "user" | "assistant" | "system";
  text: string;
}

interface AdminEvaluation {
  id: number;
  created_at: string;
  conversation_id: string;
  conversation_title?: string;
  question: string;
  answer: string;
  source_ids_json: string;
  retrieval_json: string;
  model_used: string;
  flagged: number;
  rating?: number | null;
  admin_note?: string | null;
  verification_status?: "pass" | "review" | "fail" | null;
  verification_json?: string | null;
  outcome_type?:
    | "answered"
    | "archive_gap"
    | "verification_repaired"
    | "verification_failed"
    | null;
  escalated?: number;
  escalation_reason?: string | null;
  retrieval_passes?: number;
  search_queries_json?: string;
}

interface AdminTranscript {
  conversation: AdminChatSummary;
  messages: AdminMessage[];
  evaluations: AdminEvaluation[];
}

function numberValue(value: number | string | undefined): number {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatCost(value: number): string {
  return value < 0.01
    ? `$${value.toFixed(5)}`
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      }).format(value);
}

function formatTokens(value: number): string {
  return new Intl.NumberFormat("en-US", { notation: "compact" }).format(value);
}

function parseSourceIds(value: string): string[] {
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed)
      ? parsed.filter((item): item is string => typeof item === "string")
      : [];
  } catch {
    return [];
  }
}

function AdminDashboard({ onLogout }: { onLogout: () => void }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<AdminUsageData | null>(null);
  const [chats, setChats] = useState<AdminChatSummary[]>([]);
  const [evaluations, setEvaluations] = useState<AdminEvaluation[]>([]);
  const [selectedChatId, setSelectedChatId] = useState("");
  const [transcript, setTranscript] = useState<AdminTranscript | null>(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setError("");
    archiveFetch(`/api/admin/usage?days=${days}`)
      .then(async (response) => {
        if (!response.ok) throw new Error("Usage data could not be loaded.");
        return response.json() as Promise<AdminUsageData>;
      })
      .then(setData)
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : "Admin unavailable."),
      );
  }, [days]);

  useEffect(() => {
    Promise.all([
      archiveFetch("/api/admin/chats").then(async (response) => {
        if (!response.ok) throw new Error("Conversation review could not be loaded.");
        return response.json() as Promise<AdminChatSummary[]>;
      }),
      archiveFetch("/api/admin/evaluations?limit=50").then(async (response) => {
        if (!response.ok) throw new Error("Evaluation turns could not be loaded.");
        return response.json() as Promise<AdminEvaluation[]>;
      }),
    ])
      .then(([chatRows, evaluationRows]) => {
        setChats(chatRows);
        setEvaluations(evaluationRows);
      })
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : "Review unavailable."),
      );
  }, []);

  const loadTranscript = async (id: string) => {
    setSelectedChatId(id);
    setReviewLoading(true);
    try {
      const response = await archiveFetch(`/api/admin/chats/${id}`);
      if (!response.ok) throw new Error("Transcript could not be loaded.");
      setTranscript((await response.json()) as AdminTranscript);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Transcript unavailable.");
    } finally {
      setReviewLoading(false);
    }
  };

  const toggleEvaluationFlag = async (evaluation: AdminEvaluation) => {
    const flagged = !Boolean(evaluation.flagged);
    const response = await archiveFetch(
      `/api/admin/evaluations/${evaluation.id}`,
      {
        method: "PATCH",
        body: JSON.stringify({ flagged }),
      },
    );
    if (!response.ok) {
      setError("The evaluation flag could not be saved.");
      return;
    }
    setEvaluations((items) =>
      items.map((item) =>
        item.id === evaluation.id ? { ...item, flagged: flagged ? 1 : 0 } : item,
      ),
    );
  };

  const summary = data?.summary || {};
  const inputTokens = numberValue(summary.input_tokens);
  const cachedTokens = numberValue(summary.cached_input_tokens);
  const sourceCount = numberValue(summary.source_count);
  const facebookCount = numberValue(summary.facebook_source_count);
  const cacheRate = inputTokens > 0 ? (cachedTokens / inputTokens) * 100 : 0;
  const facebookShare =
    sourceCount > 0 ? (facebookCount / sourceCount) * 100 : 0;
  const maxDailyCost = Math.max(
    0.000001,
    ...(data?.daily.map((item) => numberValue(item.cost_usd)) || [0]),
  );

  return (
    <main className="admin-shell">
      <header className="admin-header">
        <a href="/" className="landing-nav__brand">
          <ArchiveMark small />
          <span>
            <AnimatedName />
            <small>Archive operations</small>
          </span>
        </a>
        <div>
          <a href="/">Return to archive</a>
          <button type="button" onClick={onLogout}>
            <LogOut size={14} />
            Sign out
          </button>
        </div>
      </header>

      <section className="admin-content">
        <div className="admin-title">
          <div>
            <p className="eyebrow">
              <span />
              Private operations board
            </p>
            <h1>Real usage and answer quality.</h1>
            <p>
              Actual model charges, retrieval balance and administrator-only
              conversation review. Use the evidence trail to turn weak answers
              into durable evaluation cases.
            </p>
          </div>
          <label>
            Window
            <select value={days} onChange={(event) => setDays(Number(event.target.value))}>
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
              <option value={365}>1 year</option>
            </select>
          </label>
        </div>

        {error && <p className="admin-error">{error}</p>}
        {!data && !error && <div className="admin-loading">Reading usage…</div>}

        {data && (
          <>
            <div className="admin-model-strip">
              <span>Primary brain</span>
              <strong>{data.model}</strong>
              <small>Fallback: {data.fallbacks.join(" → ")}</small>
            </div>

            <div className="admin-metrics">
              <article>
                <Coins size={18} />
                <span>Actual model spend</span>
                <strong>{formatCost(numberValue(summary.cost_usd))}</strong>
                <small>{days}-day window</small>
              </article>
              <article>
                <BarChart3 size={18} />
                <span>Answered turns</span>
                <strong>{numberValue(summary.turns).toLocaleString()}</strong>
                <small>
                  {formatTokens(inputTokens)} in ·{" "}
                  {formatTokens(numberValue(summary.output_tokens))} out
                </small>
              </article>
              <article>
                <Database size={18} />
                <span>Prompt cache</span>
                <strong>{cacheRate.toFixed(1)}%</strong>
                <small>{formatTokens(cachedTokens)} cached input tokens</small>
              </article>
              <article>
                <Gauge size={18} />
                <span>Average completion</span>
                <strong>
                  {(numberValue(summary.avg_latency_ms) / 1000).toFixed(1)}s
                </strong>
                <small>End-to-end streamed response</small>
              </article>
              <article>
                <BookOpen size={18} />
                <span>Curated claim share</span>
                <strong>
                  {sourceCount > 0
                    ? (
                        (numberValue(summary.claim_source_count) / sourceCount) *
                        100
                      ).toFixed(1)
                    : "0.0"}
                  %
                </strong>
                <small>
                  {numberValue(summary.claim_source_count)} claim sources retrieved
                </small>
              </article>
              <article>
                <CircleAlert size={18} />
                <span>Facebook share</span>
                <strong>{facebookShare.toFixed(1)}%</strong>
                <small>{facebookCount} of {sourceCount} retrieved sources</small>
              </article>
            </div>

            <div className="admin-grid">
              <section className="admin-panel admin-panel--chart">
                <div className="admin-panel__heading">
                  <div>
                    <span>Daily spend</span>
                    <small>Actual USD reported by OpenRouter</small>
                  </div>
                </div>
                <div className="usage-chart">
                  {data.daily.length === 0 && <p>No recorded generations yet.</p>}
                  {data.daily.map((item) => {
                    const cost = numberValue(item.cost_usd);
                    return (
                      <div key={String(item.day)}>
                        <span>{String(item.day).slice(5)}</span>
                        <i style={{ height: `${Math.max(4, (cost / maxDailyCost) * 100)}%` }} />
                        <small>{formatCost(cost)}</small>
                      </div>
                    );
                  })}
                </div>
              </section>

              <section className="admin-panel">
                <div className="admin-panel__heading">
                  <div>
                    <span>Models actually used</span>
                    <small>Fallbacks appear separately when invoked</small>
                  </div>
                </div>
                <div className="admin-table">
                  <div className="admin-table__row admin-table__row--head">
                    <span>Model</span><span>Turns</span><span>Cost</span>
                  </div>
                  {data.models.map((item) => (
                    <div className="admin-table__row" key={String(item.model_used)}>
                      <span>{String(item.model_used)}</span>
                      <span>{numberValue(item.turns)}</span>
                      <span>{formatCost(numberValue(item.cost_usd))}</span>
                    </div>
                  ))}
                </div>
              </section>

              <section className="admin-panel admin-panel--wide">
                <div className="admin-panel__heading">
                  <div>
                    <span>Recent generations</span>
                    <small>Operational metadata only</small>
                  </div>
                </div>
                <div className="admin-table admin-table--recent">
                  <div className="admin-table__row admin-table__row--head">
                    <span>Time</span><span>Model</span><span>Tokens</span>
                    <span>Cache</span><span>Sources / FB</span><span>Cost</span>
                  </div>
                  {data.recent.map((item, index) => (
                    <div className="admin-table__row" key={`${item.created_at}-${index}`}>
                      <span>{String(item.created_at).replace("T", " ").slice(0, 16)}</span>
                      <span>{String(item.model_used)}</span>
                      <span>
                        {formatTokens(numberValue(item.input_tokens))} /{" "}
                        {formatTokens(numberValue(item.output_tokens))}
                      </span>
                      <span>{formatTokens(numberValue(item.cached_input_tokens))}</span>
                      <span>
                        {numberValue(item.source_count)} /{" "}
                        {numberValue(item.facebook_source_count)}
                      </span>
                      <span>{formatCost(numberValue(item.cost_usd))}</span>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            <div className="admin-review-grid">
              <section className="admin-panel admin-conversations">
                <div className="admin-panel__heading">
                  <div>
                    <span>Recent family conversations</span>
                    <small>Full transcripts remain inside their private chat objects</small>
                  </div>
                </div>
                <div className="admin-conversation-list">
                  {chats.length === 0 && <p>No conversations available.</p>}
                  {chats.map((chat) => (
                    <button
                      type="button"
                      className={chat.id === selectedChatId ? "is-active" : ""}
                      key={chat.id}
                      onClick={() => loadTranscript(chat.id)}
                    >
                      <strong>{chat.title}</strong>
                      <span>
                        {chat.language.toUpperCase()} ·{" "}
                        {numberValue(chat.generated_turns)} generated turns ·{" "}
                        {String(chat.updated_at).slice(0, 16)}
                      </span>
                    </button>
                  ))}
                </div>
              </section>

              <section className="admin-panel admin-transcript">
                <div className="admin-panel__heading">
                  <div>
                    <span>Transcript</span>
                    <small>Administrator-only quality inspection</small>
                  </div>
                </div>
                <div className="admin-transcript__body">
                  {reviewLoading && <p>Opening the private chat…</p>}
                  {!reviewLoading && !transcript && (
                    <p>Select a conversation to inspect what was asked and answered.</p>
                  )}
                  {!reviewLoading &&
                    transcript?.messages.map((message) => (
                      <article
                        className={`admin-message admin-message--${message.role}`}
                        key={message.id}
                      >
                        <span>{message.role}</span>
                        <p>{message.text || "No text content"}</p>
                      </article>
                    ))}
                </div>
              </section>
            </div>

            <section className="admin-panel admin-evaluations">
              <div className="admin-panel__heading">
                <div>
                  <span>Evaluation trail</span>
                  <small>
                    New turns include the exact answer and the retrieval source IDs
                  </small>
                </div>
              </div>
              <div className="admin-evaluation-list">
                {evaluations.length === 0 && (
                  <p>
                    No evaluation turns yet. Existing chats are still readable above;
                    new answers will include their retrieval trail.
                  </p>
                )}
                {evaluations.map((evaluation) => {
                  const sourceIds = parseSourceIds(evaluation.source_ids_json);
                  return (
                    <article
                      className={evaluation.flagged ? "is-flagged" : ""}
                      key={evaluation.id}
                    >
                      <header>
                        <div>
                          <span>{evaluation.model_used}</span>
                          <small>{evaluation.created_at.slice(0, 16)}</small>
                          {evaluation.verification_status && (
                            <small>
                              Evidence check: {evaluation.verification_status}
                            </small>
                          )}
                          {evaluation.outcome_type && (
                            <small>
                              Outcome: {evaluation.outcome_type.replaceAll("_", " ")}
                              {evaluation.escalated
                                ? ` · deep pass (${evaluation.escalation_reason || "policy"})`
                                : ""}
                            </small>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => toggleEvaluationFlag(evaluation)}
                        >
                          <CircleAlert size={13} />
                          {evaluation.flagged ? "Flagged" : "Flag for review"}
                        </button>
                      </header>
                      <strong>{evaluation.question}</strong>
                      <p>{evaluation.answer}</p>
                      <footer>
                        {sourceIds.map((sourceId) => (
                          <span key={sourceId}>{sourceId}</span>
                        ))}
                      </footer>
                    </article>
                  );
                })}
              </div>
            </section>

            <div className="admin-privacy">
              <LockKeyhole size={17} />
              <p>
                <strong>Stored:</strong> {data.privacy.stored}{" "}
                <strong>Excluded:</strong> {data.privacy.excluded}
              </p>
            </div>
          </>
        )}
      </section>
    </main>
  );
}

export default function App() {
  const [language, setLanguage] = useState<ArchiveLanguage>("en");
  const [authState, setAuthState] = useState<
    "loading" | "guest" | "authenticated"
  >("loading");
  const [authRole, setAuthRole] = useState<"family" | "admin">("family");
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [initialQuestion, setInitialQuestion] = useState<string | null>(null);
  const [selectedSource, setSelectedSource] = useState<ArchiveSource | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [brainLabel, setBrainLabel] = useState("Evidence-led AI");

  const active = conversations.find((item) => item.id === activeId) ?? null;

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => {
    archiveFetch("/api/auth/session")
      .then((response) => response.json())
      .then(
        (session: {
          authenticated?: boolean;
          role?: "family" | "admin";
        }) => {
          setAuthRole(session.role === "admin" ? "admin" : "family");
          setAuthState(session.authenticated ? "authenticated" : "guest");
        },
      )
      .catch(() => setAuthState("guest"));
  }, []);

  useEffect(() => {
    if (authState !== "authenticated") return;
    archiveFetch("/api/chats")
      .then((response) => (response.ok ? response.json() : []))
      .then((items: ConversationSummary[]) => {
        const biographyItems = items.map((item) => ({
          ...item,
          mode: "about" as const,
        }));
        setConversations(biographyItems);
        if (biographyItems.length > 0) {
          setActiveId(biographyItems[0].id);
          setLanguage(biographyItems[0].language);
        }
      })
      .catch(() => setConversations([]));
  }, [authState]);

  useEffect(() => {
    archiveFetch("/api/health")
      .then((response) => response.json())
      .then((health: { model?: string; privacy?: string }) => {
        if (health.model === "z-ai/glm-5.2") {
          setBrainLabel(
            health.privacy === "zero-data-retention"
              ? "GPT-5.6 Terra · archive writer · ZDR"
              : "GPT-5.6 Terra · archive writer",
          );
        } else if (health.model === "grounded-local-preview") {
          setBrainLabel("Grounded preview");
        }
      })
      .catch(() => undefined);
  }, []);

  const saveConversation = async (conversation: ConversationSummary) => {
    await archiveFetch("/api/chats", {
      method: "POST",
      body: JSON.stringify(conversation),
    }).catch(() => undefined);
  };

  const start = async (question: string) => {
    const conversation = newConversation(language, question.slice(0, 64));
    setConversations((items) => [conversation, ...items]);
    setInitialQuestion(question);
    setActiveId(conversation.id);
    await saveConversation(conversation);
  };

  const createBlank = async () => {
    const conversation = newConversation(language);
    setConversations((items) => [conversation, ...items]);
    setInitialQuestion(null);
    setActiveId(conversation.id);
    setSidebarOpen(false);
    await saveConversation(conversation);
  };

  const patchConversation = async (
    id: string,
    patch: Partial<ConversationSummary>,
  ) => {
    setConversations((items) =>
      items.map((item) =>
        item.id === id
          ? { ...item, ...patch, updatedAt: new Date().toISOString() }
          : item,
      ),
    );
    await archiveFetch(`/api/chats/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }).catch(() => undefined);
  };

  const deleteConversation = async (id: string) => {
    setConversations((items) => items.filter((item) => item.id !== id));
    if (activeId === id) {
      setActiveId(null);
      setInitialQuestion(null);
    }
    await archiveFetch(`/api/chats/${id}`, { method: "DELETE" }).catch(
      () => undefined,
    );
  };

  const logout = async () => {
    await archiveFetch("/api/auth/logout", { method: "POST" }).catch(
      () => undefined,
    );
    setConversations([]);
    setActiveId(null);
    setSelectedSource(null);
    setAuthRole("family");
    setAuthState("guest");
  };

  if (authState === "loading") {
    return (
      <main className="access-gate access-gate--loading">
        <ArchiveMark />
      </main>
    );
  }

  if (authState === "guest") {
    return (
      <LoginGate
        language={language}
        setLanguage={setLanguage}
        onAuthenticated={(role) => {
          setAuthRole(role);
          setAuthState("authenticated");
        }}
      />
    );
  }

  if (window.location.pathname === "/admin") {
    if (authRole === "admin") return <AdminDashboard onLogout={logout} />;
    return (
      <main className="access-gate">
        <section className="access-card">
          <LockKeyhole size={22} />
          <p className="eyebrow"><span />Administrator access</p>
          <h1>This board is private.</h1>
          <p>Sign out and enter the administrator access code to continue.</p>
          <button type="button" onClick={logout}>Sign out</button>
          <a href="/">Return to archive</a>
        </section>
      </main>
    );
  }

  if (!active) {
    return (
      <Landing
        language={language}
        setLanguage={setLanguage}
        onStart={start}
        onLogout={logout}
        isAdmin={authRole === "admin"}
      />
    );
  }

  return (
    <main className={`chat-shell ${selectedSource ? "has-evidence" : ""}`}>
      <ConversationSidebar
        conversations={conversations}
        activeId={active.id}
        onSelect={(id) => {
          setInitialQuestion(null);
          setActiveId(id);
          const next = conversations.find((item) => item.id === id);
          if (next) setLanguage(next.language);
        }}
        onNew={createBlank}
        onDelete={deleteConversation}
        language={language}
        mobileOpen={sidebarOpen}
        setMobileOpen={setSidebarOpen}
        onLogout={logout}
        isAdmin={authRole === "admin"}
      />
      <ChatSession
        key={active.id}
        conversation={active}
        initialQuestion={initialQuestion}
        onInitialQuestionSent={() => setInitialQuestion(null)}
        onLanguageChange={(nextLanguage) =>
          patchConversation(active.id, { language: nextLanguage })
        }
        onTitle={(title) => patchConversation(active.id, { title })}
        onSelectSource={setSelectedSource}
        language={language}
        setLanguage={setLanguage}
        openSidebar={() => setSidebarOpen(true)}
        brainLabel={brainLabel}
      />
      {selectedSource && (
        <EvidencePanel
          source={selectedSource}
          onClose={() => setSelectedSource(null)}
          language={language}
        />
      )}
    </main>
  );
}
